import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  HStack,
  Spinner,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import {
  getSectorCategories,
  getAllSectors,
} from "../actions/sector/sectorActions";

const MotionBox = motion(Box);

const float = {
  initial: { y: 0 },
  animate: {
    y: [0, -10, 0],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" },
  },
};

const Hero = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    categories = [],
    categoriesLoading = false,
    data: products = [],
    loading: productsLoading = false,
  } = useSelector((state) => state.sectorSmmReducer || {});

  useEffect(() => {
    if (!categories.length) {
      dispatch(getSectorCategories());
    }
  }, [dispatch]);

  useEffect(() => {
    if (categories.length > 0 && products.length === 0) {
      dispatch(getAllSectors(categories[0]));
    }
  }, [categories, dispatch]);

  const sectorId = products?.[0]?.id;

  const handleExplore = () => {
    if (sectorId) {
      navigate(`/sector/${sectorId}/details`);
    }
  };

  const isLoading = categoriesLoading || productsLoading;

  return (
    <Box
      position="relative"
      w="full"
      pt="40"
      pb="32"
      bgGradient="linear(to-b, #E6EDF4, #EEF3F7, #F6F8FA)"
      overflow="hidden"
    >
      <Box position="absolute" inset="0" bg="transparent" />

      <Container maxW="6xl" textAlign="center" position="relative" px="6">
        
        <MotionBox
          variants={float}
          initial="initial"
          animate="animate"
          position="absolute"
          top="10"
          left="10"
          bg="white"
          shadow="lg"
          px="5"
          py="2"
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          color="gray.600"
          fontSize="sm"
          display="flex"
          alignItems="center"
          gap="2"
        >
          <Box w="2" h="2" rounded="full" bg="blue.500" />
          Competitor Signal
        </MotionBox>

        <MotionBox
          variants={float}
          initial="initial"
          animate="animate"
          transition={{ delay: 0.5 }}
          position="absolute"
          top="-20"
          right="10"
          bg="white"
          shadow="lg"
          px="5"
          py="2"
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          color="gray.600"
          fontSize="sm"
          display="flex"
          alignItems="center"
          gap="2"
        >
          <Box w="2" h="2" rounded="full" bg="teal.500" />
          Market Category
        </MotionBox>

        <Box
          position="absolute"
          top="-10"
          left="35%"
          bgGradient="linear(to-b, #E6EDF4, #EEF3F7, #F6F8FA)"
          opacity="0.9"
          px="6"
          py="3"
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          color="green.800"
          fontWeight="bold"
          fontSize="sm"
          display="flex"
          alignItems="center"
          gap="2"
        >
          <Box w="2" h="2" rounded="full" bg="green.600" />
          Decision Intelligence for Manufacturing
        </Box>

        <MotionBox
          variants={float}
          initial="initial"
          animate="animate"
          transition={{ delay: 1.3 }}
          position="absolute"
          bottom="20"
          right="20"
          bg="white"
          shadow="lg"
          px="5"
          py="2"
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          color="gray.600"
          fontSize="sm"
          display="flex"
          alignItems="center"
          gap="2"
        >
          <Box w="2" h="2" rounded="full" bg="green.500" />
          Decision Point
        </MotionBox>

        <Heading
          fontSize={{ base: "4xl", md: "6xl" }}
          fontFamily="serif"
          fontWeight="bold"
          color="#1A2C4E"
          lineHeight="1.2"
          mt="20"
        >
          Market clarity for <br />
          <Box as="span" color="#2AA198">
            better decisions
          </Box>
        </Heading>

        <Text
          mt="6"
          fontSize={{ base: "lg", md: "xl" }}
          color="gray.600"
          maxW="3xl"
          mx="auto"
          lineHeight="tall"
        >
          Transform public market data into clear, actionable insights.
          Understand your category, track competitors, and make confident
          decisions without complex dashboards.
        </Text>

        <HStack mt="10" spacing="6" justify="center">

          {/* FIXED BUTTON */}
          <Button
            onClick={handleExplore}
            isDisabled={!sectorId || isLoading}
            bg="#2AA198"
            color="white"
            px={8}
            py={6}
            borderRadius="xl"
            _hover={{ bg: "#1F7A73" }}
            rightIcon={
              isLoading ? <Spinner size="sm" /> : <ArrowForwardIcon />
            }
          >
            {isLoading ? "Loading..." : "Explore Insights"}
          </Button>

          <Link to="/sample">
            <Button
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              px="8"
              py="6"
              borderRadius="xl"
              fontWeight="medium"
              color="#2AA198"
              _hover={{ bg: "gray.100" }}
              rightIcon={<ArrowForwardIcon />}
            >
              View Sample Insight
            </Button>
          </Link>

        </HStack>
      </Container>
    </Box>
  );
};

export default Hero;